import { useCallback, useRef } from 'react';
import { isEmpty } from 'lodash';
import config from '../../authentication/config';
import { gtmTracker } from '../../gtmTracker';
import { cleanEventBeforeSending } from '../utils/cleanEventBeforeSending';

export const useChatAgentMessageSender = ({ client }) => {
    const agentWindowRef = useRef([]);
    const registerAgent = useCallback(srcWindow => {
        agentWindowRef.current.push(srcWindow);
    }, []);
    const unregisterAgent = useCallback(srcWindow => {
        agentWindowRef.current = agentWindowRef.current.filter(src => src !== srcWindow);
    }, []);
    const clearWindowList = useCallback(() => {
        agentWindowRef.current = [];
    }, []);
    const gridApiRef = useRef(null);
    const wizardApiRef = useRef([]);
    const messageActionListenersRef = useRef([]);
    const registerGrid = useCallback(gridApi => {
        gridApiRef.current = gridApi;
        return () => (gridApiRef.current = null);
    }, []);
    const setCellToEdit = useCallback(cell => {
        if (gridApiRef.current) {
            gridApiRef.current.setCellToEdit(cell);
        }
    }, []);
    const registerWizard = useCallback(wizardApi => {
        wizardApiRef.current.push(wizardApi);
        return () => (wizardApiRef.current = wizardApiRef.current.filter(item => item !== wizardApi));
    }, []);
    const setWizardStep = useCallback(step => {
        if (!isEmpty(wizardApiRef.current)) {
            wizardApiRef.current.forEach(wizardApi => wizardApi.setWizardStep(step));
        }
    }, []);
    const registerChatTextMessageListener = useCallback(callback => {
        messageActionListenersRef.current.push(callback);

        return () => {
            messageActionListenersRef.current = messageActionListenersRef.current.filter(el => el !== callback);
        };
    }, []);
    const notifyChatTextMessageListeners = useCallback(messageAction => {
        messageActionListenersRef.current.forEach(callback => callback(messageAction));
    }, []);

    const sendResponseToChatAgent = useCallback(
        (message, source) => {
            if (isEmpty(agentWindowRef.current)) {
                gtmTracker.trackError(new Error('Chat Agent is not connected. Ignoring message:', message));
                return;
            }

            const messageObject = {
                client,
                ...cleanEventBeforeSending(message),
            };

            if (source) {
                source.postMessage(messageObject, config.getChatAgentDomain());
                return;
            }

            agentWindowRef.current.forEach(srcWindow => {
                srcWindow.postMessage(messageObject, config.getChatAgentDomain());
            });
        },
        [client]
    );

    return {
        registerAgent,
        unregisterAgent,
        clearWindowList,
        sendResponseToChatAgent,
        registerGrid,
        setCellToEdit,
        registerWizard,
        setWizardStep,
        registerChatTextMessageListener,
        notifyChatTextMessageListeners,
    };
};
